import { useEffect, useState } from 'react'
import {
  STATIONS,
  currentStations,
  setLiveStations,
  type Station,
} from './stations'

const CACHE_KEY = 'music24.landingStations.v1'
const DEFAULT_ICON = '/station-art/station_upbeat.png'

type RemoteRow = {
  id?: string
  name?: string
  stream_url?: string
  icon_url?: string | null
}

type CachedRow = {
  id: string
  name: string
  streamUrl: string
  iconUrl: string | null
}

const listeners = new Set<() => void>()
let inflight: Promise<void> | null = null

function knownById(id: string): Station | undefined {
  return STATIONS.find((station) => station.streamId === id)
}

function copyFor(name: string, known: Station | undefined): Pick<Station, 'summary' | 'description' | 'keywords'> {
  if (known) {
    return { summary: known.summary, description: known.description, keywords: known.keywords }
  }
  return {
    summary: `${name} on Music24 — free Jewish music radio, live 24/6.`,
    description: `Listen to ${name} on Music24, free Jewish music radio streaming live 24/6. Open ${name} in the browser or in the Music24 app — no ads and no subscription. This station comes from the live Music24 catalog, with its own page so you can find it and press play.`,
    keywords: [`${name} radio`, `${name} jewish music`, 'jewish music stream', 'music24'],
  }
}

export function stationsFromRows(rows: CachedRow[]): Station[] {
  const used = new Set<string>()
  const stations: Station[] = []
  for (const row of rows) {
    if (!row.id || !row.name || !row.streamUrl.startsWith('http')) continue
    const known = knownById(row.id)
    let slug = known?.slug ?? row.id.replace(/_/g, '-')
    if (used.has(slug)) slug = `${slug}-${row.id}`
    used.add(slug)
    const icon = row.iconUrl || known?.icon || DEFAULT_ICON
    stations.push({
      slug,
      name: row.name,
      streamId: row.id,
      streamUrl: row.streamUrl,
      icon,
      ...copyFor(row.name, known),
    })
  }
  return stations
}

function readCache(): Station[] | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as CachedRow[]
    if (!Array.isArray(parsed) || parsed.length === 0) return null
    const stations = stationsFromRows(parsed)
    return stations.length > 0 ? stations : null
  } catch {
    return null
  }
}

function writeCache(rows: CachedRow[]) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(rows))
  } catch {
    // private mode
  }
}

const cached = readCache()
if (cached) setLiveStations(cached)

function publish(stations: Station[]) {
  setLiveStations(stations)
  listeners.forEach((listener) => listener())
}

async function pullStations(): Promise<void> {
  const base = import.meta.env.VITE_SUPABASE_URL?.replace(/\/$/, '')
  const key = import.meta.env.VITE_SUPABASE_ANON_KEY
  if (!base || !key) return

  try {
    const response = await fetch(
      `${base}/rest/v1/stations?select=id,name,stream_url,icon_url&is_active=eq.true&order=sort_order.asc`,
      {
        headers: { apikey: key, Authorization: `Bearer ${key}` },
        signal: AbortSignal.timeout(4000),
      },
    )
    if (!response.ok) return
    const body = (await response.json()) as RemoteRow[]
    if (!Array.isArray(body) || body.length === 0) return

    const rows: CachedRow[] = []
    for (const row of body) {
      const id = (row.id ?? '').trim()
      const name = (row.name ?? '').trim()
      const streamUrl = (row.stream_url ?? '').trim()
      if (!id || !name || !streamUrl.startsWith('http')) continue
      const iconUrl = (row.icon_url ?? '').trim()
      rows.push({ id, name, streamUrl, iconUrl: iconUrl.startsWith('http') ? iconUrl : null })
    }
    const stations = stationsFromRows(rows)
    if (stations.length === 0) return
    writeCache(rows)
    publish(stations)
  } catch {
    // keep bundled or last cache
  }
}

export function refreshStations(): Promise<void> {
  if (!inflight) inflight = pullStations().finally(() => { inflight = null })
  return inflight
}

export function useStations(): Station[] {
  const [stations, setStations] = useState(currentStations)
  useEffect(() => {
    const sync = () => setStations(currentStations())
    listeners.add(sync)
    void refreshStations()
    const onVisible = () => {
      if (document.visibilityState === 'visible') void refreshStations()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      listeners.delete(sync)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [])
  return stations
}
